import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, Facebook, Instagram, Linkedin, Youtube, ArrowRight } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import ContactDropdown from './ContactDropdown';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedLink from './LocalizedLink';
import useLocale from '../hooks/useLocale';
import useCommonContent from '../hooks/useCommonContent';
import { getLocalizedPath } from '../data/i18nRoutes';
import { useLocalizedServices } from '../data/services';

const Header = ({ isDark, toggleTheme }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const locale = useLocale();
  const { labels, t } = useCommonContent(locale);
  const services = useLocalizedServices(locale);
  const pathFor = (frPath) => getLocalizedPath(frPath, locale);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const isActivePath = (frPath) => {
    const localized = pathFor(frPath);
    if (frPath === '/') {
      return location.pathname === '/' || location.pathname === '/en';
    }
    return location.pathname.startsWith(localized) || location.pathname.startsWith(frPath);
  };

  const isExactPath = (frPath) => location.pathname === pathFor(frPath) || location.pathname === frPath;

  const navLinkClass = (active) =>
    `px-4 py-2 text-sm rounded-lg transition-colors whitespace-nowrap ${active
      ? 'text-blue-600 dark:text-blue-400 font-bold bg-blue-50 dark:bg-blue-900/20'
      : 'font-medium text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400'
    }`;

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled
      ? 'bg-white/95 dark:bg-slate-950/95 backdrop-blur-md shadow-lg border-b border-slate-200/50 dark:border-slate-800/50'
      : 'bg-white dark:bg-slate-950'
      }`}>
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-8 overflow-visible">
        <div className="flex items-center justify-between h-16 lg:h-20 min-w-0 overflow-visible">

          <LocalizedLink to="/" className="flex-shrink-0">
            <img
              src={require('../Assets/TIGUIDOU_CREATION WEB-Logo-Oficial-Quebecstore_Inc-www_quebecstore_ca.png')}
              alt={t('header.logoAlt')}
              className="h-10 sm:h-12 lg:h-14 w-auto"
            />
          </LocalizedLink>

          <nav className="hidden lg:flex items-center gap-1 min-w-0 flex-1 justify-center mx-2">
            <LocalizedLink to="/" className={navLinkClass(location.pathname === '/' || location.pathname === '/en')}>
              {labels.home}
            </LocalizedLink>

            <div className="group">
              <button type="button" className={`flex items-center gap-1 ${navLinkClass(isActivePath('/services'))}`}>
                {labels.services} <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform" />
              </button>

              <div className="absolute left-[2vw] right-[2vw] xl:left-1/2 xl:right-auto xl:-translate-x-1/2 top-full w-auto xl:w-[1400px] bg-gradient-to-b from-blue-50 to-white dark:from-slate-900 dark:to-slate-950 rounded-b-2xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] border-4 border-blue-600 dark:border-blue-500 p-4 lg:p-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 border-t-0">
                <div className="mb-4 pb-3 border-b border-blue-200/70 dark:border-blue-900/60">
                  <LocalizedLink to="/services/" className="flex items-center justify-center gap-3 py-2 px-4 text-sm lg:text-base font-bold text-blue-700 dark:text-blue-300 hover:bg-blue-100/80 dark:hover:bg-blue-900/30 rounded-xl transition-colors">
                    {labels.allServices} <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5" />
                  </LocalizedLink>
                </div>
                <div className="grid grid-cols-4 lg:grid-cols-7 gap-0 rounded-xl border border-blue-200 dark:border-blue-900/70 overflow-hidden [&>*]:border-r [&>*]:border-b [&>*]:border-blue-200/70 dark:[&>*]:border-blue-900/60">
                  {services.map((service, idx) => (
                    <LocalizedLink
                      key={idx}
                      to={service.href}
                      className="group/icon flex flex-col items-center justify-start gap-2 lg:gap-3 p-3 lg:px-2 lg:py-4 bg-white/80 dark:bg-slate-900/60 hover:bg-blue-100 dark:hover:bg-blue-950/80 transition-colors h-full"
                      title={service.desc}
                    >
                      <div className={`p-2.5 lg:p-3 rounded-xl transition-colors flex-shrink-0 bg-white dark:bg-slate-800 ${service.color || 'group-hover/icon:bg-blue-800'}`}>
                        <service.icon className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600 dark:text-blue-400 group-hover/icon:text-white transition-colors" />
                      </div>
                      <p className="text-[11px] lg:text-[13px] font-semibold text-center leading-snug text-slate-900 dark:text-slate-100 px-1 mt-auto">
                        {service.name}
                      </p>
                    </LocalizedLink>
                  ))}
                </div>
              </div>
            </div>

            <LocalizedLink to="/aica" className={`hidden xl:block ${navLinkClass(isActivePath('/aica'))}`}>
              {labels.aica}
            </LocalizedLink>

            <LocalizedLink
              to="/services/conception-site-web-sherbrooke"
              className={navLinkClass(isExactPath('/services/conception-site-web-sherbrooke'))}
            >
              {labels.webDesign}
            </LocalizedLink>

            <LocalizedLink
              to="/Tiguidou-web-Partenaire-Shopify-vente-enligne"
              className={navLinkClass(isExactPath('/Tiguidou-web-Partenaire-Shopify-vente-enligne'))}
            >
              {labels.shopify}
            </LocalizedLink>

            <LocalizedLink
              to="/a-propos-agence-web-sherbrooke"
              className={navLinkClass(isActivePath('/a-propos-agence-web-sherbrooke'))}
            >
              {labels.about}
            </LocalizedLink>

            <LocalizedLink to="/blogue-nouvelle" className={`hidden xl:block ${navLinkClass(isActivePath('/blogue'))}`}>
              {labels.blog}
            </LocalizedLink>

            <LocalizedLink
              to="/contact-agence-web-sherbrooke"
              className={navLinkClass(isActivePath('/contact-agence-web-sherbrooke'))}
            >
              {labels.contact}
            </LocalizedLink>

            <LocalizedLink
              to="/soumission-projet-web-gratuit"
              className="ml-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              {labels.quote}
            </LocalizedLink>
          </nav>

          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <LanguageSwitcher />
            <div className="hidden xl:block">
              <ThemeToggle isDark={isDark} toggle={toggleTheme} />
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 lg:hidden flex-shrink-0">
            <ContactDropdown />
            <button
              type="button"
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="p-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 touch-manipulation"
              aria-label="Menu"
            >
              {isMobileOpen ? <X size={20} className="sm:w-6 sm:h-6" /> : <Menu size={20} className="sm:w-6 sm:h-6" />}
            </button>
          </div>
        </div>
      </div>

      {isMobileOpen && (
        <div className="lg:hidden bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 shadow-xl max-h-[calc(100vh-4rem)] overflow-y-auto w-full">
          <div className="px-3 sm:px-4 py-4 space-y-1 max-w-full overflow-x-hidden">
            <LocalizedLink to="/" className={`block px-4 py-3 rounded-xl font-medium ${location.pathname === '/' || location.pathname === '/en' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
              {labels.home}
            </LocalizedLink>

            <LocalizedLink to="/services" className={`block px-4 py-3 rounded-xl font-medium ${isActivePath('/services') ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
              {labels.services}
            </LocalizedLink>

            <LocalizedLink to="/aica" className={`block px-4 py-3 rounded-xl font-medium ${isActivePath('/aica') ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
              {labels.aica}
            </LocalizedLink>

            <LocalizedLink to="/services/conception-site-web-sherbrooke" className={`block px-4 py-3 rounded-xl font-medium ${isExactPath('/services/conception-site-web-sherbrooke') ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
              {labels.webDesign}
            </LocalizedLink>

            <LocalizedLink to="/Tiguidou-web-Partenaire-Shopify-vente-enligne" className={`block px-4 py-3 rounded-xl font-medium ${isExactPath('/Tiguidou-web-Partenaire-Shopify-vente-enligne') ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
              {labels.shopify}
            </LocalizedLink>

            <LocalizedLink to="/a-propos-agence-web-sherbrooke" className={`block px-4 py-3 rounded-xl font-medium ${isActivePath('/a-propos-agence-web-sherbrooke') ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
              {labels.about}
            </LocalizedLink>

            <LocalizedLink to="/blogue-nouvelle" className={`block px-4 py-3 rounded-xl font-medium ${isActivePath('/blogue') ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
              {labels.blog}
            </LocalizedLink>

            <LocalizedLink to="/contact-agence-web-sherbrooke" className={`block px-4 py-3 rounded-xl font-medium ${isActivePath('/contact-agence-web-sherbrooke') ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
              {labels.contact}
            </LocalizedLink>

            <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-800 space-y-3">
              <a href="tel:819-452-4163" className="flex items-center gap-3 px-4 py-2 text-slate-700 dark:text-slate-300">
                <Phone className="w-5 h-5 text-blue-600" />
                <span className="font-medium">819-452-4163</span>
              </a>
              <a href="mailto:sales@quebecstore.ca" className="flex items-center gap-3 px-4 py-2 text-slate-700 dark:text-slate-300">
                <Mail className="w-5 h-5 text-blue-600" />
                <span className="font-medium">sales@quebecstore.ca</span>
              </a>
            </div>

            <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between px-4 gap-3">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <a href="https://facebook.com/people/Tiguidou-Creation-web/61576478671852/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-blue-600" aria-label="Facebook">
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a href="https://instagram.com/quebecstore_tiguidou" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-pink-600" aria-label="Instagram">
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a href="https://linkedin.com/company/tiguidou" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-blue-700" aria-label="LinkedIn">
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a href="https://youtube.com/@QuebecDefender" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-red-600" aria-label="YouTube">
                    <Youtube className="w-5 h-5" />
                  </a>
                  <LanguageSwitcher className="ml-1" />
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <ThemeToggle isDark={isDark} toggle={toggleTheme} />
                </div>
              </div>
            </div>

            <div className="pt-4">
              <LocalizedLink
                to="/soumission-projet-web-gratuit"
                className="block w-full text-center py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold"
              >
                {labels.freeQuote}
              </LocalizedLink>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
